import { PageTitle } from '_metronic/layout/core'
import { KTSVG } from '_metronic/helpers'

import { SkeletonFullWidthWithContent } from 'Presentation/Components/Loading/Skeleton'
import KumopackStepper from 'Presentation/Components/KumopackStepper'
import StepChip from 'Presentation/Components/StepChip'
import { ClientSideTable } from 'Presentation/Components/Table'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'

import useViewModel from './ViewModel'
import moment from 'moment-timezone'
import { isMobileDevice } from '_metronic/assets/ts/_utils'
import clsx from 'clsx'

//////////
// View //
//////////

const OEMProductionDetailView: React.FC = () => {
	const isHorizontal = !isMobileDevice()
	const {
		billingStatus,
		approveSampleStatus,
		producingStatus,
		deliverStatus,
		cancelStatus,
		isSubmitting,
		isLoading,
		canDeliverSample,
		canDeliverProduct,
		PRODUCTION_TABLE_CONFIGS,
		OTHER_TABLE_CONFIGS,
		items,
		otherItems,
		data,
		error,
		deliveryAddress,
		deliverFormState,
		onChangeDeliverFormState,
		onSubmitDeliveryForm,
	} = useViewModel()

	return (
		<ViewPageTitle code={data?.code}>
			<div className="row gy-5">
				{/* begin:: Stepper & Status */}
				<div className="col-12">
					<div className="card border border-kumopack-grey-200">
						<div className="card-header">
							<div className="card-title">
								<div className="d-inline-block fw-bold me-2">หมายเลขการผลิต: </div>
								<div className="d-inline-block">{data?.code}</div>
							</div>
							<div className="card-toolbar">
								<StepChip stepLabel={data?.statusTitleTh} />
							</div>
						</div>

						<div className="card-body">
							<div className="row gy-10">
								<div className="col-12">
									<div className="card border border-kumopack-grey-200">
										<div className="card-body p-1">
											<KumopackStepper
												color="secondary"
												items={[
													{
														label: 'ส่งคำขอ',
														status: 'done',
														description: data?.quotation?.inquiry?.oemRequest?.createdAt
															? moment(data?.quotation?.inquiry?.oemRequest?.createdAt)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},

													{
														label: 'เลือกผู้ผลิต',
														status: 'done',
														description: data?.quotation?.inquiry?.createdAt
															? moment(data?.quotation?.inquiry?.createdAt)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},
													{
														label: 'ชำระเงิน',
														status: billingStatus.status,
														description: billingStatus.date
															? moment(billingStatus.date)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},
													{
														label: 'ได้รับตัวอย่าง',
														status: approveSampleStatus.status,
														description: approveSampleStatus.date
															? moment(approveSampleStatus.date)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},
													{
														label: 'ผลิตสินค้า',
														status: producingStatus,
														description: '-',
													},
													{
														label: 'จัดส่งสำเร็จ',
														status: deliverStatus.status,
														description: deliverStatus.date
															? moment(deliverStatus.date)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},
													{
														label: 'ยกเลิก',
														status: cancelStatus.status,
														description: cancelStatus.date
															? moment(cancelStatus.date)
																	.tz('Asia/Bangkok')
																	.format('DD-MM-YYYY HH:mm')
															: '-',
													},
													{
														label: 'ข้อพิพาท',
														status: 'pending',
														description: '-',
													},
												]}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				{/* end:: Stepper & Status */}

				{/* start:: Delivery Form */}
				{isLoading ? (
					<div className="col-12">
						<SkeletonFullWidthWithContent />
					</div>
				) : canDeliverSample || canDeliverProduct ? (
					<div className="col-12">
						<div className="card border border-kumopack-grey-200">
							<div className="card-header">
								<div className="card-title">
									<h4 className="text-kumopack-grey-900 fw-bold mb-0">กรอกรายละเอียดการขนส่ง</h4>
									<span
										className="d-inline-block badge badge-kumopack-secondary-50 text-kumopack-secondary-700"
										style={{ borderRadius: '16px' }}>
										{canDeliverSample ? 'ตัวอย่าง' : 'สินค้า'}
									</span>
								</div>
							</div>

							<div className="card-body">
								<div className="row mb-10">
									<label className="form-label col-12 col-xl-3 mb-5 mb-xl-0 d-flex flex-column">
										<div>ที่อยู่ในการจัดส่ง</div>
									</label>
									<div className="col-12 col-xl-9 mb-0">
										<span
											className="fs-5 fw-bold"
											dangerouslySetInnerHTML={{ __html: deliveryAddress }}></span>
									</div>
								</div>

								<FormGenerator
									formKey="tracking"
									inputType="plain"
									label="หมายเลขพัสดุ"
									// additionalLabelCom={<span className="required" />}
									containerClassName="mb-5"
									isHorizontal={isHorizontal}
									value={deliverFormState.tracking}
									onChange={e => onChangeDeliverFormState('tracking', e.target.value)}
									isShowValid={!!error?.tracking}
									additionalComInput={
										error?.tracking ? (
											<div className="fv-plugins-message-container mt-2">
												<div className="fv-help-block">{error.tracking}</div>
											</div>
										) : undefined
									}
								/>

								{/* begin:: Evidence Picture */}
								<div className="row mb-5">
									<label className="form-label col-12 col-xl-3 mb-5 mb-xl-0 d-flex flex-column">
										<div className="">
											รูปภาพประกอบ <span className="required"></span>
										</div>
										<div className="text-kumopack-grey-600 fs-8">(Recommended 800x400px)</div>
									</label>
									<div className="col-12 col-xl-9 mb-0">
										<div
											className={clsx(
												'w-100 rounded bg-kumopack-base-white border mb-5 text-center shadow d-flex align-items-center justify-content-center position-relative',
												{
													'border-kumopack-error-400': error?.evidencePicture,
												}
											)}
											style={{ height: '320px' }}>
											{deliverFormState.evidencePicture && (
												<div
													className="btn btn-sm btn-icon btn-danger btn-active-light-danger position-absolute"
													style={{ top: -10, right: -10 }}
													onClick={() => onChangeDeliverFormState('evidencePicture', null)}>
													<KTSVG
														className="svg-icon-1"
														path="/media/icons/duotune/general/gen027.svg"
													/>
												</div>
											)}
											{deliverFormState.evidencePicture && (
												<img
													className="mx-auto h-100 w-100 rounded"
													src={
														typeof deliverFormState.evidencePicture !== 'string'
															? URL.createObjectURL(deliverFormState.evidencePicture as any)
															: deliverFormState.evidencePicture
													}
													onError={(e: any) => {
														e.target.onerror = null
														e.target.src = '/media/icons/kumopack/default-placeholder.png'
													}}
													style={{
														objectFit: 'contain',
														userSelect: 'none',
														pointerEvents: 'none',
													}}
													alt="Factory Cover"
												/>
											)}
											{!deliverFormState.evidencePicture && (
												<div className={'fw-bold fs-5 text-kumopack-grey-300 py-10'}>
													No Files Uploaded
												</div>
											)}
										</div>

										<FormGenerator
											formKey="supplier-delivery-form-drag-and-drop-evidence-picture"
											inputType="drag-and-drop"
											multiple={false}
											accept="image/*"
											libAccept={{
												'image/png': [],
												'image/jpeg': [],
												'image/jpg': [],
											}}
											onFileUpload={coverFiles =>
												onChangeDeliverFormState('evidencePicture', coverFiles[0])
											}
										/>

										{error?.evidencePicture && (
											<div className="fv-plugins-message-container">
												<div className="fv-help-block">{error?.evidencePicture}</div>
											</div>
										)}
									</div>
								</div>
								{/* end:: Evidence Picture */}
							</div>

							<div className="card-footer text-end">
								<button
									className="btn btn-sm btn-kumopack-secondary-700 text-kumopack-base-white fw-bold"
									disabled={isLoading || isSubmitting}
									onClick={onSubmitDeliveryForm}>
									ยืนยัน
								</button>
							</div>
						</div>
					</div>
				) : (
					<></>
				)}
				{/* end:: Delivery Form */}

				{/* start:: Items */}
				<div className="col-12">
					{isLoading ? (
						<SkeletonFullWidthWithContent />
					) : (
						<div className="card border border-kumopack-grey-200">
							<div className="card-header">
								<div className="card-title">
									<h4 className="text-kumopack-grey-900 fw-bold mb-0">รายการผลิต</h4>
									<span
										className="d-inline-block badge badge-kumopack-secondary-50 text-kumopack-secondary-700"
										style={{ borderRadius: '16px' }}>
										{items.length} รายการ
									</span>
								</div>
							</div>

							<div className="card-body p-0">
								<ClientSideTable
									isGrey
									isLoading={isLoading}
									data={items}
									columns={PRODUCTION_TABLE_CONFIGS}
									items_per_page={9999}
								/>
							</div>
						</div>
					)}
				</div>
				{/* end:: Items */}

				{/* start:: Other Items */}
				<div className="col-12">
					{isLoading ? (
						<SkeletonFullWidthWithContent />
					) : (
						<div className="card border border-kumopack-grey-200">
							<div className="card-header">
								<div className="card-title">
									<h4 className="text-kumopack-grey-900 fw-bold mb-0">รายการอื่นๆ</h4>
									<span
										className="d-inline-block badge badge-kumopack-secondary-50 text-kumopack-secondary-700"
										style={{ borderRadius: '16px' }}>
										{otherItems.length} รายการ
									</span>
								</div>
							</div>

							<div className="card-body p-0">
								<ClientSideTable
									isGrey
									isLoading={isLoading}
									data={otherItems}
									columns={OTHER_TABLE_CONFIGS}
									items_per_page={9999}
								/>
							</div>
						</div>
					)}
				</div>
				{/* end:: Other Items */}
			</div>
		</ViewPageTitle>
	)
}

/////////////////////
// View Page Title //
/////////////////////

const ViewPageTitle: React.FC<{ children: React.ReactNode; code: string }> = ({
	children,
	code,
}) => {
	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: 'OEM',
						path: '/oem',
						isActive: false,
						isSeparator: false,
					},
					{
						title: 'Production',
						path: '/oem/productions',
						isActive: false,
						isSeparator: false,
					},
					{
						title: 'Detail',
						path: '#',
						isActive: false,
						isSeparator: false,
					},
				]}>
				{`การผลิต OEM - ${code ?? ''}`}
			</PageTitle>

			{children}
		</>
	)
}

export default OEMProductionDetailView
